import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import { MoveLeft, Clock, GraduationCap } from "lucide-react";
import Button from "../Shared/Button";
import courses from "../../assets/Courses.jpg";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import Spinner from "../Shared/Spinner";
import { getCourses } from "../../api/Courses/GetAllCourses";
import { useEffect, useState } from "react";
import { getUserCountry, hasDiscount } from "../../utils/utils";
import { AddToCart } from "../../api/Cart/AddCourseToCard";
import { queryClient } from "../../main";
import { getApiErrorMessage } from "../../utils/apiError";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getCoursesOffline } from "../../api/OfflineCourses/GetAllOfflineCourses";
import { Calendar } from "lucide-react";
import { useUserDataStore } from "../../stores/user";
import { getAccessToken } from "../../lib/session";
const Courses = () => {
  const [country, setCountry] = useState<string | null>(null);
  const navigate = useNavigate();
  const { userData } = useUserDataStore();
  const { data: CoursesResponse, isLoading: isCoursesLoading } = useQuery({
    queryFn: getCourses,
    queryKey: ["courses"],
    staleTime: 0,
    refetchInterval: 0,
  });
  const { data: CoursesResponseOffline, isLoading: isCoursesOfflineLoading } = useQuery({
    queryFn: getCoursesOffline,
    queryKey: ["coursesOffline"],
    staleTime: 0,
    refetchInterval: 0,
  });
  const offlineCourses = CoursesResponseOffline?.data?.coursesData || [];
  const coursesData = CoursesResponse?.data?.coursesData || [];
  const { mutate } = useMutation({
    mutationFn: (courseId: string) => AddToCart(courseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Error Adding To Cart"));
    },
  });

  const handleBuyNowClick = (id: string) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      mutate(id);
    } else {
      navigate("/login?cart=true");
    }
  };

  useEffect(() => {
    const userCountry = getUserCountry();
    setCountry(userCountry);
  }, []);

  return (
    <div className="pb-40 mb-20 sm:mb-40">
      <div className="md:h-[690px] h-[600px] w-full relative">
        <div className="absolute inset-0 z-10 bg-gradientBg opacity-70 size-full" />
        <div className="absolute inset-0 w-full h-full">
          <img
            src={courses}
            className="object-cover object-top w-full h-full"
            alt=""
          />
        </div>
        <div className="relative z-50 sm:space-y-5 space-y-2.5 text-center text-white pt-20">
          <h1 className="text-sm font-semibold sm:text-base">Our courses</h1>
          <h2 className="md:text-4xl sm:text-3xl text-2xl max-w-[370px] font-semibold mx-auto">
            Enroll Now!
          </h2>
          <p className="max-w-[764px] mx-auto sm:text-base text-sm">
            Take the next step in your career and gain the skills you need to
            succeed. Our courses are designed to empower you with practical
            knowledge and real-world expertise. Join us and start your journey
            today!
          </p>
        </div>
        <div className="relative z-[999999] container lg:px-10 sm:px-3 sm:mt-16 mt-12">
          <Swiper
            grabCursor={true}
            navigation={{
              nextEl: ".custom-next",
              prevEl: ".custom-prev",
            }}
            loop={true}
            slidesPerView={3}
            spaceBetween={30}
            autoplay={{
              delay: 50,
              disableOnInteraction: true,
            }}
            modules={[Autoplay, Navigation]}
            speed={3000}
            className="mySwiper"
            breakpoints={{
              0: {
                slidesPerView: 1.2,
                spaceBetween: 10,
              },
              550: {
                slidesPerView: 1.6,
                spaceBetween: 10,
              },
              640: {
                slidesPerView: 2,
                spaceBetween: 12,
              },
              768: {
                slidesPerView: 2.5,
                spaceBetween: 20,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 25,
              },
            }}
          >
            {isCoursesLoading || isCoursesOfflineLoading ? (
              <div className="min-h-[50vh] flex items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <>
                {coursesData.map((slide) => (
                  <SwiperSlide key={slide.id} className="relative !h-auto">
                    <div className="relative h-full">
                      <Link to={`/course/${slide.slug}`}>
                        <img
                          src={slide.img_url}
                          className="sm:h-[237px] h-[210px] w-full relative object-fit"
                          alt=""
                        />
                      </Link>
                      <div className="bg-white flex sm:h-[calc(100%-237px)] h-[calc(100%-210px)] flex-col justify-end text-white border-[#B5B5C380] border">
                        <div className="m-3 border-b sm:m-4 border-black/40">
                          <Link className="w-full" to={`/course/${slide.slug}`}>
                            <h1 className="font-semibold sm:text-lg text-primary">
                              {slide.name}
                            </h1>
                          </Link>

                          <div className="justify-between flex items-center sm:my-4 my-2.5">
                            <div>
                              <div className="font-semibold text-secondary sm:text-lg">
                                {country === "EG" ? (
                                  <>
                                    {hasDiscount(slide.actualPrice_egp, slide.discountPrice_egp)
                                      ? slide.discountPrice_egp
                                      : slide.actualPrice_egp}
                                    <span className="text-xs uppercase md:text-sm">
                                      EGP
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    {hasDiscount(slide.actualPrice_usd, slide.discountPrice_usd)
                                      ? slide.discountPrice_usd
                                      : slide.actualPrice_usd}
                                    <span className="text-xs uppercase md:text-sm">
                                      USD
                                    </span>
                                  </>
                                )}
                              </div>
                              {(country === "EG"
                                  ? hasDiscount(slide.actualPrice_egp, slide.discountPrice_egp) && (
                                    <div className="line-through text-[#7F7E97] sm:text-sm text-xs">
                                      {slide.actualPrice_egp} EGP
                                    </div>
                                  )
                                  : hasDiscount(slide.actualPrice_usd, slide.discountPrice_usd) && (
                                    <div className="line-through text-[#7F7E97] sm:text-sm text-xs">
                                      {slide.actualPrice_usd} USD
                                    </div>
                                  ))}
                            </div>
                            {slide.part && (
                              <div className="flex items-center justify-center w-16 text-sm text-white rounded h-7 bg-secondary md:text-base">
                                Part {slide.part}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mb-2 md:mb-4">
                            <img
                              src={slide.Instructor?.photo}
                              className="md:max-w-5  md:max-h-5 max-w-[18px] max-h-[18px] w-full h-full object-cover rounded-full object-[75%_25%]"
                              alt="Instructor Avatar"
                            />
                            <Link
                              to={`/instructor/${slide.InstructorId}`}
                              className="text-[#1D1D1F] font-medium md:text-xs text-[10px]"
                            >
                              {slide.Instructor?.firstName}{" "}
                              {slide.Instructor?.lastName}{" "}
                              <span className="opacity-70">from</span>{" "}
                              {slide.Instructor?.city},{" "}
                              {slide.Instructor?.country}
                            </Link>
                          </div>
                        </div>
                        <div className="sm:my-3 my-1.5 sm:mx-4 mx-3">
                          <div className="flex items-center justify-center gap-2.5">
                            {/* <div className="flex gap-1.5 items-center">
                                                    <Book size={24} className="text-[#7F7E97] sm:w-6 w-5" />
                                                    <span className="text-sm font-medium text-primary sm:text-base">{slide.chapters}</span>
                                                </div> 
                                                <div className="size-1 bg-[#8C9AB4] rounded-full" />
                                                */}
                            <div className="flex gap-1.5 items-center">
                              <Clock
                                size={24}
                                className="text-[#7F7E97] sm:w-6 w-5"
                              />
                              <span className="text-sm font-medium text-primary sm:text-base">
                                {slide.num_hours}h
                              </span>
                            </div>
                            <div className="size-1 bg-[#8C9AB4] rounded-full" />

                            <div className="flex gap-1.5 items-center">
                              <GraduationCap
                                size={24}
                                className="text-[#7F7E97] sm:w-6 w-5"
                              />
                              <span className="text-sm font-medium text-primary sm:text-base">
                                {slide._count.students}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2 my-5">
                            <Link
                              to={`/course/${slide.slug}`}
                              className="w-full"
                            >
                              <Button
                                variant="primary"
                                className="w-full h-8 text-xs md:h-9 "
                              >
                                Apply Now
                              </Button>
                            </Link>
                            <Button
                              variant="gray"
                              className="md:h-9 h-8 !rounded-lg font-semibold text-xs w-full"
                              onClick={() => handleBuyNowClick(slide.id)}
                              disabled={userData.role === "sales"}
                            >
                              Add To Cart
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
                {offlineCourses.map((slide) => (
                  <SwiperSlide key={slide.id} className="relative !h-auto">
                    <div className="relative h-full">
                      <Link to={`/offline-course/${slide.id}`}>
                        <img
                          src={slide.img_url}
                          className="sm:h-[237px] h-[210px] w-full relative object-fit"
                          alt=""
                        />
                      </Link>
                      {slide.isLive ? (
                        <div className="absolute top-2 px-1.5 py-2 left-2 flex items-center gap-1 bg-white rounded">
                          <div className="bg-green-500 rounded-full size-2" />
                          <span className="text-sm font-semibold text-secondary">Live Sessions</span>
                        </div>

                      ) : (
                        <div className="absolute top-2 px-1.5 py-2 left-2 flex items-center gap-1 bg-white rounded">
                          <div className="bg-red-500 rounded-full size-2" />
                          <span className="text-sm font-semibold text-secondary">Offline Sessions</span>
                        </div>

                      )}

                      <div className="bg-white flex sm:h-[calc(100%-237px)] h-[calc(100%-210px)] flex-col justify-end text-white border-[#B5B5C380] border">
                        <div className="m-3 border-b sm:m-4 border-black/40">
                          <Link className="w-full" to={`/offline-course/${slide.id}`} >
                            <h1 className="font-semibold sm:text-lg text-primary">
                              {slide.name}
                            </h1>
                          </Link>

                          <div className="justify-between flex items-center sm:my-4 my-2.5">
                            <div>
                              <div className="font-semibold text-secondary sm:text-lg">
                                {country === "EG" ? (
                                  <>
                                    {hasDiscount(slide.actualPrice_egp, slide.discountPrice_egp)
                                      ? slide.discountPrice_egp
                                      : slide.actualPrice_egp}
                                    <span className="text-xs uppercase md:text-sm">
                                      EGP
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    {hasDiscount(slide.actualPrice_usd, slide.discountPrice_usd)
                                      ? slide.discountPrice_usd
                                      : slide.actualPrice_usd}
                                    <span className="text-xs uppercase md:text-sm">
                                      USD
                                    </span>
                                  </>
                                )}
                              </div>
                              {(country === "EG"
                                  ? hasDiscount(slide.actualPrice_egp, slide.discountPrice_egp) && (
                                    <div className="line-through text-[#7F7E97] sm:text-sm text-xs">
                                      {slide.actualPrice_egp} EGP
                                    </div>
                                  )
                                  : hasDiscount(slide.actualPrice_usd, slide.discountPrice_usd) && (
                                    <div className="line-through text-[#7F7E97] sm:text-sm text-xs">
                                      {slide.actualPrice_usd} USD
                                    </div>
                                  ))}
                            </div>
                            {slide.part && (
                              <div className="flex items-center justify-center w-16 text-sm text-white rounded h-7 bg-secondary md:text-base">
                                Part {slide.part}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mb-2 md:mb-4">
                            <img
                              src={slide.Instructor?.photo}
                              className="md:max-w-5  md:max-h-5 max-w-[18px] max-h-[18px] w-full h-full object-cover rounded-full object-[75%_25%]"
                              alt="Instructor Avatar"
                            />
                            <Link
                              to={`/offline-course/${slide.id}`}
                              className="text-[#1D1D1F] font-medium md:text-xs text-[10px]"
                            >
                              {slide.Instructor?.firstName}{" "}
                              {slide.Instructor?.lastName}{" "}
                              <span className="opacity-70">from</span>{" "}
                              {slide.Instructor?.city},{" "}
                              {slide.Instructor?.country}
                            </Link>
                          </div>
                        </div>
                        <div className="sm:my-3 my-1.5 sm:mx-4 mx-3">
                          <div className="flex items-center justify-center gap-2.5">
                            {/* <div className="flex gap-1.5 items-center">
                                                    <Book size={24} className="text-[#7F7E97] sm:w-6 w-5" />
                                                    <span className="text-sm font-medium text-primary sm:text-base">{slide.chapters}</span>
                                                </div> 
                                                <div className="size-1 bg-[#8C9AB4] rounded-full" />
                                                */}
                            <div className="flex gap-1.5 items-center">
                              <Clock
                                size={24}
                                className="text-[#7F7E97] sm:w-6 w-5"
                              />
                              <span className="text-sm font-medium text-primary sm:text-base">
                                {slide.num_hours}h
                              </span>
                            </div>
                            <div className="size-1 bg-[#8C9AB4] rounded-full" />

                            <div className="flex gap-1.5 items-center">
                              <Calendar
                                size={20}
                                className="text-[#7F7E97] sm:w-6 w-5"
                              />
                              <span className="text-sm font-medium text-primary sm:text-base">
                                {slide.startDate}
                              </span>
                            </div>

                          </div>
                          <div className="flex gap-2 my-5">
                            <Link
                              to={`/offline-course/${slide.id}`} className="w-full"
                            >
                              <Button
                                variant="primary"
                                className="w-full h-8 text-xs md:h-9 "
                              >
                                Apply Now
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}

              </>
            )}

          </Swiper>
          <div className="flex justify-center mx-auto sm:gap-5 gap-2.5 items-center sm:mt-7 mt-5 w-full">
            <button className="custom-prev hover:bg-secondary transition-all duration-300 bg-white border border-secondary text-white flex items-center justify-center sm:size-[55px] size-12 rounded-full group">
              <MoveLeft
                className="w-6 text-secondary group-hover:text-white sm:w-8"
                size={32}
              />
            </button>
            <div>
              <div className="custom-pagination2"></div>
            </div>
            <button className="custom-next hover:bg-secondary transition-all duration-300 bg-white border border-secondary text-white flex items-center justify-center sm:size-[55px] size-12 rounded-full group">
              <MoveLeft
                className="w-6 rotate-180 text-secondary group-hover:text-white sm:w-8"
                size={32}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Courses;
